import { useEffect, useMemo, useState } from "react";
import { splitTransaction } from "./TransactionService";
import { getAllAccounts } from "../accounts/AccountService";
import { getAllCategories } from "../categories/CategoryService";
import { getAllMerchants } from "../merchants/MerchantService";
import {
  buildExpenseFormInitialValues,
  buildIncomeFormInitialValues,
  formatAccountLabel,
  formatCurrency,
  normalizeTransactionType,
  readErrorMessage,
} from "./transactionUtils";
import "./SplitTransactionForm.css";

function emptyLine() {
  return { categoryId: "", amount: "", description: "" };
}

export default function SplitTransactionForm({ initialTransaction, onSaved, onCancel }) {
  const type = normalizeTransactionType(initialTransaction?.transactionType || "EXPENSE");
  const defaults = type === "INCOME"
    ? buildIncomeFormInitialValues(initialTransaction)
    : buildExpenseFormInitialValues(initialTransaction);

  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [base, setBase] = useState({
    accountId: defaults.accountId || "",
    merchantId: defaults.merchantId || "",
    date: defaults.date || "",
    description: defaults.description || "",
  });
  const [lines, setLines] = useState(() => buildInitialLines(initialTransaction));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const nextDefaults = type === "INCOME"
      ? buildIncomeFormInitialValues(initialTransaction)
      : buildExpenseFormInitialValues(initialTransaction);
    setBase({
      accountId: nextDefaults.accountId || "",
      merchantId: nextDefaults.merchantId || "",
      date: nextDefaults.date || "",
      description: nextDefaults.description || "",
    });
    setLines(buildInitialLines(initialTransaction));
  }, [initialTransaction, type]);

  const load = async () => {
    const [accountsData, categoriesData, merchantsData] = await Promise.all([
      getAllAccounts(),
      getAllCategories(),
      getAllMerchants(),
    ]);
    setAccounts(Array.isArray(accountsData) ? accountsData : []);
    setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    setMerchants(Array.isArray(merchantsData) ? merchantsData : []);
  };

  const filteredCategories = useMemo(() => {
    const wantExpense = type !== "INCOME";
    return categories.filter((category) => {
      const value = String(category.categoryType || "").toLowerCase();
      return wantExpense ? value !== "income" : value !== "expense";
    });
  }, [categories, type]);

  const targetTotal = Number(initialTransaction?.transactionAmount || 0);
  const splitTotal = lines.reduce((sum, line) => sum + Number(line.amount || 0), 0);
  const difference = targetTotal ? splitTotal - targetTotal : 0;

  const updateLine = (index, field, value) => {
    setLines((current) => current.map((line, lineIndex) => (lineIndex === index ? { ...line, [field]: value } : line)));
  };

  const addLine = () => setLines((current) => [...current, emptyLine()]);
  const removeLine = (index) => setLines((current) => (current.length === 1 ? current : current.filter((_, lineIndex) => lineIndex !== index)));

  const submit = async (event) => {
    event.preventDefault();
    if (!initialTransaction?.id) {
      alert("Select an existing transaction from the list to split it.");
      return;
    }
    if (!base.accountId || !base.date) {
      alert("Account and date are required.");
      return;
    }
    if (lines.some((line) => !line.categoryId || Number(line.amount || 0) <= 0)) {
      alert("Every split line needs a category and a positive amount.");
      return;
    }
    if (targetTotal && Math.abs(difference) > 0.01) {
      alert("Split amounts must match the original transaction total.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await splitTransaction({
        transactionId: initialTransaction?.id,
        lines: lines.map((line) => ({
          categoryId: line.categoryId,
          merchantId: base.merchantId || null,
          amount: line.amount,
          description: [base.description, line.description].filter(Boolean).join(" • "),
          tags: [],
        })),
      });
      if (!response.ok) {
        alert(await readErrorMessage(response, "Split transaction not saved."));
        return;
      }
      alert("Transaction split saved.");
      onSaved?.();
    } catch (error) {
      alert(error?.message || "Split transaction not saved.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="split-transaction-form" onSubmit={submit}>
      <div className="split-transaction-header">
        <div>
          <h2>{initialTransaction ? "Split transaction" : "Split entry"}</h2>
          <p>
            {initialTransaction
              ? `Break one ${type.toLowerCase()} into multiple categorized lines.`
              : "Select a transaction from the transaction list to split it."}
          </p>
        </div>
        {initialTransaction && <div className="split-transaction-total">Original total: {formatCurrency(targetTotal)}</div>}
      </div>

      <div className="split-transaction-base">
        <select className="input" value={base.accountId} onChange={(e) => setBase((current) => ({ ...current, accountId: e.target.value }))}>
          <option value="">Select account</option>
          {accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {formatAccountLabel(account)}
            </option>
          ))}
        </select>

        {type !== "INCOME" && (
          <select className="input" value={base.merchantId} onChange={(e) => setBase((current) => ({ ...current, merchantId: e.target.value }))}>
            <option value="">Select merchant</option>
            {merchants.map((merchant) => (
              <option key={merchant.id} value={merchant.id}>
                {merchant.merchantName}
              </option>
            ))}
          </select>
        )}

        <input className="input" type="date" value={base.date} onChange={(e) => setBase((current) => ({ ...current, date: e.target.value }))} />
        <input
          className="input"
          value={base.description}
          onChange={(e) => setBase((current) => ({ ...current, description: e.target.value }))}
          placeholder="Shared description"
        />
      </div>

      <div className="split-transaction-lines">
        {lines.map((line, index) => (
          <div key={`split-line-${index}`} className="split-transaction-line">
            <select className="input" value={line.categoryId} onChange={(e) => updateLine(index, "categoryId", e.target.value)}>
              <option value="">Select category</option>
              {filteredCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.categoryName}
                </option>
              ))}
            </select>

            <input
              className="input"
              type="number"
              min="0.01"
              step="0.01"
              value={line.amount}
              onChange={(e) => updateLine(index, "amount", e.target.value)}
              placeholder="Amount"
            />

            <input
              className="input"
              value={line.description}
              onChange={(e) => updateLine(index, "description", e.target.value)}
              placeholder="Line description"
            />

            <button type="button" className="split-transaction-remove" onClick={() => removeLine(index)}>
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className="split-transaction-footer">
        <button type="button" className="btn" onClick={addLine}>Add line</button>
        {targetTotal > 0 && (
          <div className={`split-transaction-diff ${Math.abs(difference) > 0.01 ? "invalid" : ""}`}>
            Current split total {formatCurrency(splitTotal)} • Difference {formatCurrency(difference)}
          </div>
        )}
      </div>

      <div className="split-transaction-actions">
        {onCancel && <button type="button" className="btn" onClick={onCancel}>Cancel</button>}
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? "Saving..." : "Save Split"}
        </button>
      </div>
    </form>
  );
}

function buildInitialLines(initialTransaction) {
  if (!initialTransaction) return [emptyLine(), emptyLine()];
  return [
    {
      categoryId: initialTransaction.categoryId || "",
      amount: initialTransaction.transactionAmount || "",
      description: initialTransaction.description || "",
    },
  ];
}
